// API helpers for the cloud Ontology Editor.
//
// Reads the declared schema (classes + properties) of a graph from
// GET /api/v1/ontology/schema and exposes create/update/delete mutations
// for classes and properties. Mirrors useVocab.ts: a react-query read hook
// plus plain async mutation functions the page invokes and then invalidates.
//
// The schema is read straight from the schema graph (NOT the VDB), so a
// just-authored term appears immediately and an edit round-trips exactly.

import { useAnonymousRead } from '@/lib/usePages';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiFetch } from './api';
import { baseClassLocalName } from './baseClassColors';
import { scopeRequests } from './useGraphScope';
import type { TagInput } from './tags';

export type PropertyKind = 'object' | 'datatype';

/** One annotation statement on a schema term, in the canonical native
 *  predicate vocabulary. `lang` preserves an RDF language tag across a
 *  read -> edit -> write round trip (QROS values carry none). */
export interface AnnotationValue {
  predicate: string;
  value: string;
  lang?: string | null;
}

/** The predicates the class/property annotation list manages (one
 *  replace-set). Labels have their own field and are not listed. */
export const TERM_ANNOTATION_PREDICATES = [
  ':comment',
  ':definition',
  ':scopeNote',
  ':altLabel',
  ':note',
  ':example',
] as const;

/** The subset a vocabulary concept's annotation list manages — alt labels
 *  keep their dedicated field there. */
export const CONCEPT_ANNOTATION_PREDICATES = [
  ':comment',
  ':definition',
  ':scopeNote',
  ':note',
  ':example',
] as const;

/** Plain-language names for the annotation predicates (selector labels). */
export const ANNOTATION_PREDICATE_LABELS: Record<string, string> = {
  ':comment': 'Comment',
  ':definition': 'Definition',
  ':scopeNote': 'Scope note',
  ':altLabel': 'Alternative label',
  ':note': 'Note',
  ':example': 'Example',
};

export interface SchemaClass {
  uri: string;
  label: string | null;
  /** Scalar convenience: the first :comment. Editors use `annotations`. */
  comment: string | null;
  /** Every managed annotation statement (comment/definition/scopeNote/...). */
  annotations: AnnotationValue[];
  /** Direct rdfs:subClassOf parents (class IRIs). Empty = a top class. */
  super_classes: string[];
}

export interface SchemaProperty {
  uri: string;
  label: string | null;
  /** Scalar convenience: the first :comment. Editors use `annotations`. */
  comment: string | null;
  /** Every managed annotation statement (comment/definition/scopeNote/...). */
  annotations: AnnotationValue[];
  kind: PropertyKind;
  domain: string[];
  range: string[];
}

export interface OntologySchema {
  classes: SchemaClass[];
  properties: SchemaProperty[];
  /** The ontology declaration in scope (names the editor), when one exists. */
  ontology: { uri: string; label: string | null; version: string | null } | null;
}

// The ontology reads with whatever the visitor has: a token, or nothing on a
// site that serves signed-out readers. Gating on the token alone left the
// Ontology page empty for anonymous readers (issue #103) while the API
// allowed the read.
export function useOntologySchema(graph: string | null, accessToken: string | undefined) {
  const anonymousRead = useAnonymousRead();
  const canRead = !!accessToken || anonymousRead.data === true;
  return useQuery<OntologySchema>({
    queryKey: ['ontology', 'schema', graph ?? '', accessToken ? 'auth' : 'noauth'],
    enabled: !!graph && canRead,
    queryFn: async () => {
      if (!graph) {
        throw new Error('graph required');
      }
      const url = `/api/v1/ontology/schema?graph=${encodeURIComponent(graph)}`;
      const r = await apiFetch<OntologySchema>(url, accessToken);
      if (!r.ok) {
        throw new Error(r.message);
      }
      return r.data;
    },
  });
}

/**
 * The declared schema across a SET of graphs. `/api/v1/ontology/schema` takes
 * one graph, so a multi-graph scope fans out — one request per graph — and the
 * declarations are merged here; the all-graphs union stays a single request.
 * A term declared in more than one graph appears once, with the union of its
 * parents / domain / range / annotations, which is what reading the same set
 * through the union graph reports.
 */
export function useOntologySchemas(graphs: string[], accessToken: string | undefined) {
  const targets = scopeRequests(graphs);
  const anonymousRead = useAnonymousRead();
  const canRead = !!accessToken || anonymousRead.data === true;
  const results = useQueries({
    queries: targets.map((graph) => ({
      queryKey: ['ontology', 'schema', graph, accessToken ? 'auth' : 'noauth'],
      enabled: canRead,
      queryFn: async (): Promise<OntologySchema> => {
        const url = `/api/v1/ontology/schema?graph=${encodeURIComponent(graph)}`;
        const r = await apiFetch<OntologySchema>(url, accessToken);
        if (!r.ok) {
          throw new Error(r.message);
        }
        return r.data;
      },
    })),
  });

  const pages = results.filter((r) => r.data !== undefined).map((r) => r.data as OntologySchema);
  const settled = results.length > 0 && pages.length === results.length;
  // The merged schema MUST keep its identity between renders. React compares by
  // identity, so rebuilding an equal-but-new object on every render told every
  // consumer the ontology had changed: in the editor that rebuilt the class
  // forest, rebuilt the filtered forest, and re-fired the effect that expands
  // the tree, which set state and started the next render. With a search term
  // present that loop never settles (an empty needle returns the SAME array, so
  // it never started), and a navigation held in a React transition is starved
  // until the term is cleared — a menu that ignores clicks until you clear the
  // search box (list 27.29). Keyed on each page's own data reference, which
  // react-query keeps stable until that graph's schema actually changes.
  // Keyed on when each graph's schema last changed, which react-query stamps
  // and which moves only on a real fetch. A dependency list must keep one
  // shape, so the stamps are joined rather than spread.
  const freshness = results.map((r) => r.dataUpdatedAt).join(',');
  const data = useMemo(
    () => (settled ? mergeSchemas(pages) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `pages` is rebuilt every render by design; `freshness` is what actually changes
    [settled, freshness],
  );
  return {
    data,
    isFetching: results.some((r) => r.isFetching),
    isError: results.some((r) => r.isError),
    isSuccess: settled,
    error: (results.find((r) => r.error)?.error ?? null) as Error | null,
  };
}

function mergeStrings(a: string[], b: string[]): string[] {
  return [...new Set([...a, ...b])];
}

function mergeAnnotations(a: AnnotationValue[], b: AnnotationValue[]): AnnotationValue[] {
  const seen = new Set(a.map((x) => `${x.predicate} ${x.value} ${x.lang ?? ''}`));
  const out = [...a];
  for (const x of b) {
    const key = `${x.predicate} ${x.value} ${x.lang ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(x);
  }
  return out;
}

function mergeSchemas(pages: OntologySchema[]): OntologySchema {
  const classes = new Map<string, SchemaClass>();
  const properties = new Map<string, SchemaProperty>();
  let ontology: OntologySchema['ontology'] = null;
  for (const page of pages) {
    if (!ontology) ontology = page.ontology;
    for (const c of page.classes) {
      const prev = classes.get(c.uri);
      classes.set(
        c.uri,
        prev
          ? {
              uri: c.uri,
              label: prev.label ?? c.label,
              comment: prev.comment ?? c.comment,
              annotations: mergeAnnotations(prev.annotations, c.annotations),
              super_classes: mergeStrings(prev.super_classes, c.super_classes),
            }
          : c,
      );
    }
    for (const p of page.properties) {
      const prev = properties.get(p.uri);
      properties.set(
        p.uri,
        prev
          ? {
              uri: p.uri,
              label: prev.label ?? p.label,
              comment: prev.comment ?? p.comment,
              annotations: mergeAnnotations(prev.annotations, p.annotations),
              kind: prev.kind,
              domain: mergeStrings(prev.domain, p.domain),
              range: mergeStrings(prev.range, p.range),
            }
          : p,
      );
    }
  }
  return { classes: [...classes.values()], properties: [...properties.values()], ontology };
}

// ---- create ----

export interface CreateClassRequest {
  graph: string;
  label: string;
  comment?: string;
  super_classes?: string[];
  /** Block tags for the definition (QROS-native; ignored on RDF). */
  tags?: TagInput[];
  /** The author's chosen identity. Omit to let the server derive it from the
   *  label — that derivation is the form's default, not a verdict. A URI
   *  already in use comes back as a 409, never a silent `Foo2`. */
  uri?: string;
}

export interface CreatePropertyRequest {
  graph: string;
  label: string;
  comment?: string;
  kind: PropertyKind;
  domain?: string[];
  range?: string[];
  /** Block tags for the definition (QROS-native; ignored on RDF). */
  tags?: TagInput[];
  /** See {@link CreateClassRequest.uri}. */
  uri?: string;
}

export interface MintResponse {
  uri: string;
}

export async function createClass(body: CreateClassRequest, accessToken: string) {
  return apiFetch<MintResponse>('/api/v1/ontology/class', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function createProperty(body: CreatePropertyRequest, accessToken: string) {
  return apiFetch<MintResponse>('/api/v1/ontology/property', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface CreateTagRequest {
  graph: string;
  /** Bare tag name (leading `@` accepted); becomes `@name` everywhere. */
  name: string;
  label?: string;
  /** text | number | date | entity | flag */
  value_kind?: string;
}

/** Declare a reusable statement tag — it then appears in every tag-editor
 *  catalog dropdown. QROS-native; the RDF backend rejects. */
export async function createTag(body: CreateTagRequest, accessToken: string) {
  return apiFetch<MintResponse>('/api/v1/ontology/tag', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---- update ----
//
// `undefined` leaves a field unchanged. For `super_classes` / `domain` /
// `range`, a present array (including `[]`) replaces the whole set. For
// `annotations`, a present array replaces EVERY managed annotation statement
// with exactly the list (`[]` clears them all). `label` must be non-empty
// if sent.

export interface PatchClassRequest {
  graph: string;
  uri: string;
  label?: string;
  annotations?: AnnotationValue[];
  super_classes?: string[];
  /** Block tags applied to the rewritten statements (QROS-native, additive). */
  tags?: TagInput[];
}

export interface PatchPropertyRequest {
  graph: string;
  uri: string;
  label?: string;
  annotations?: AnnotationValue[];
  kind?: PropertyKind;
  domain?: string[];
  range?: string[];
  /** Block tags applied to the rewritten statements (QROS-native, additive). */
  tags?: TagInput[];
}

export async function patchClass(body: PatchClassRequest, accessToken: string) {
  return apiFetch<null>('/api/v1/ontology/class', accessToken, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function patchProperty(body: PatchPropertyRequest, accessToken: string) {
  return apiFetch<null>('/api/v1/ontology/property', accessToken, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// ---- delete ----
//
// Server returns 409 with a human-readable `detail` when the term still has
// dependents (subclasses / instances / a property referencing the class;
// instance usage for a property). The SPA surfaces that detail verbatim.

export async function deleteClass(graph: string, uri: string, accessToken: string) {
  const url = `/api/v1/ontology/class?graph=${encodeURIComponent(graph)}&uri=${encodeURIComponent(uri)}`;
  return apiFetch<null>(url, accessToken, { method: 'DELETE' });
}

export async function deleteProperty(graph: string, uri: string, accessToken: string) {
  const url = `/api/v1/ontology/property?graph=${encodeURIComponent(graph)}&uri=${encodeURIComponent(uri)}`;
  return apiFetch<null>(url, accessToken, { method: 'DELETE' });
}

// ---- identity control (URI visibility, duplicates, rename) ----

/** How much of the store references a term — the blast radius shown before a
 *  rename. `as_subject` is the term's own statements (always moved);
 *  everything else is what points AT it. */
export interface TermReferences {
  as_subject: number;
  as_predicate: number;
  as_object: number;
  in_scope: number;
  rules: string[];
}

/** References from ELSEWHERE — the number that decides cheap vs cascade. */
export function incomingReferences(refs: TermReferences): number {
  return refs.as_predicate + refs.as_object + refs.in_scope;
}

export async function termReferences(uri: string, accessToken: string) {
  const url = `/api/v1/ontology/references?uri=${encodeURIComponent(uri)}`;
  return apiFetch<TermReferences>(url, accessToken);
}

export interface RenameTermRequest {
  graph: string;
  uri: string;
  new_uri: string;
}

/** Rename a term. The server rewrites every statement and rule carrying the
 *  old URI in one atomic write; renaming onto an existing URI is a 409. */
export async function renameTerm(body: RenameTermRequest, accessToken: string) {
  return apiFetch<TermReferences>('/api/v1/ontology/rename', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Split a URI into its namespace (through the last `#`, `/`, or the leading
 *  `:`) and local name. Mirrors the backend's `namespace_of`, so the create
 *  form's two fields recompose to exactly the URI the server would store. */
export function splitUri(uri: string): { namespace: string; local: string } {
  if (uri.startsWith(':')) return { namespace: ':', local: uri.slice(1) };
  const idx = Math.max(uri.lastIndexOf('#'), uri.lastIndexOf('/'));
  if (idx < 0) return { namespace: '', local: uri };
  return { namespace: uri.slice(0, idx + 1), local: uri.slice(idx + 1) };
}

/** Slugify a label into a local name: PascalCase for classes, camelCase for
 *  properties, acronym tails preserved. Mirrors the backend `slugify` so the
 *  create form can PREVIEW the URI live — the server stays authoritative, and
 *  the URI it returns is what the editor then shows. */
export function slugifyLocalName(label: string, kind: 'class' | 'property'): string {
  const words = label.split(/[^\p{L}\p{N}]+/u).filter((w) => w !== '');
  return words
    .map((w, i) => {
      const first = w[0] ?? '';
      const rest = w.slice(1);
      const head = i === 0 && kind === 'property' ? first.toLowerCase() : first.toUpperCase();
      return head + rest;
    })
    .join('');
}

/** The namespace new terms should default into: the most common one across
 *  the schema's own (non-foundation) classes and properties, ties broken by
 *  the longer then lexically-lower namespace. Mirrors the backend's
 *  `derive_schema_namespace`; falls back to the clean `:` home prefix. */
export function deriveSchemaNamespace(schema: OntologySchema | undefined): string {
  const counts = new Map<string, number>();
  for (const term of [...(schema?.classes ?? []), ...(schema?.properties ?? [])]) {
    // The foundation preamble is not the author's namespace (backend rule:
    // a base class is `:Name` / `qros:Name`, never a full-IRI term).
    if (
      (term.uri.startsWith(':') || term.uri.startsWith('qros:')) &&
      baseClassLocalName(term.uri) !== null
    ) {
      continue;
    }
    const { namespace, local } = splitUri(term.uri);
    if (!namespace || !local) continue;
    counts.set(namespace, (counts.get(namespace) ?? 0) + 1);
  }
  let best = ':';
  let bestCount = 0;
  for (const [ns, count] of counts) {
    if (
      count > bestCount ||
      (count === bestCount && (ns.length > best.length || (ns.length === best.length && ns < best)))
    ) {
      best = ns;
      bestCount = count;
    }
  }
  return bestCount === 0 ? ':' : best;
}

// ---- derived views (pure helpers) ----

/** A class plus its child classes, assembled from the flat schema. */
export interface ClassTreeNode {
  cls: SchemaClass;
  children: ClassTreeNode[];
}

/**
 * Build the subClassOf forest from the flat class list. Roots are classes
 * whose `super_classes` are all empty or point outside the declared set
 * (e.g. owl:Thing or an imported class). A class with multiple in-set
 * parents appears under each. Cycle-safe via a per-branch visited set.
 */
export function buildClassForest(classes: SchemaClass[]): ClassTreeNode[] {
  const byUri = new Map(classes.map((c) => [c.uri, c]));
  const childrenOf = new Map<string, SchemaClass[]>();
  const roots: SchemaClass[] = [];
  for (const c of classes) {
    const inSetParents = c.super_classes.filter((p) => byUri.has(p));
    if (inSetParents.length === 0) {
      roots.push(c);
    } else {
      for (const p of inSetParents) {
        const list = childrenOf.get(p) ?? [];
        list.push(c);
        childrenOf.set(p, list);
      }
    }
  }
  const build = (cls: SchemaClass, visited: Set<string>): ClassTreeNode => {
    const kids = (childrenOf.get(cls.uri) ?? [])
      .filter((k) => !visited.has(k.uri))
      .map((k) => {
        const next = new Set(visited);
        next.add(k.uri);
        return build(k, next);
      });
    kids.sort((a, b) => labelOf(a.cls).localeCompare(labelOf(b.cls)));
    return { cls, children: kids };
  };
  const forest = roots.map((r) => build(r, new Set([r.uri])));
  forest.sort((a, b) => labelOf(a.cls).localeCompare(labelOf(b.cls)));
  return forest;
}

/** Display label for a term: its rdfs:label, else the local name of the URI. */
export function labelOf(term: { uri: string; label: string | null }): string {
  return term.label ?? localName(term.uri);
}

/** Display fallback for an unlabeled term: the segment after the last `#`,
 *  `/`, or prefix `:` — with underscores read as spaces, so a native term
 *  like `:Studio_Capability` renders "Studio Capability", never the raw
 *  token. Display only; never use the result as an identifier. */
export function localName(uri: string): string {
  const idx = Math.max(uri.lastIndexOf('#'), uri.lastIndexOf('/'), uri.lastIndexOf(':'));
  const name = idx >= 0 && idx < uri.length - 1 ? uri.slice(idx + 1) : uri;
  return name.replace(/_/g, ' ');
}

/** Properties whose declared domain includes `classUri`. */
export function propertiesForClass(
  properties: SchemaProperty[],
  classUri: string,
): SchemaProperty[] {
  return properties.filter((p) => p.domain.includes(classUri));
}

/** Every class the given class sits under, itself included. */
function withAncestors(classUri: string, classes: SchemaClass[]): Set<string> {
  const byUri = new Map(classes.map((c) => [c.uri, c]));
  const out = new Set<string>();
  const stack = [classUri];
  while (stack.length > 0) {
    const uri = stack.pop() as string;
    if (out.has(uri)) continue;
    out.add(uri);
    for (const parent of byUri.get(uri)?.super_classes ?? []) stack.push(parent);
  }
  return out;
}

/** The properties a class's instances can carry: those whose domain is the
 *  class or a class above it. With none declared, every property, so a
 *  class whose domains were never written can still be broken down. */
export function propertiesOfClass(
  classUri: string,
  schema: { classes: SchemaClass[]; properties: SchemaProperty[] },
): { properties: SchemaProperty[]; declared: boolean } {
  const lineage = withAncestors(classUri, schema.classes);
  const declared = schema.properties.filter((p) => p.domain.some((d) => lineage.has(d)));
  const byLabel = (a: SchemaProperty, b: SchemaProperty) => labelOf(a).localeCompare(labelOf(b));
  return declared.length > 0
    ? { properties: [...declared].sort(byLabel), declared: true }
    : { properties: [...schema.properties].sort(byLabel), declared: false };
}

/** Count every class node across the forest (incl. multi-parent repeats). */
export function countForest(forest: ClassTreeNode[]): number {
  return forest.reduce((acc, n) => acc + 1 + countForest(n.children), 0);
}
